import Vue from 'vue';

const SEPARATOR_CHAR = '-';
const MODIFIER_PREFIX = '   + ';
// Columns an 80mm thermal printer fits at font A — the same width the Worldline
// cardholder receipt printed underneath uses.
const DEFAULT_LINE_LENGTH = 48;

function formatAmount(amount) {
  return (Number(amount) || 0).toFixed(2);
}

// Number of columns a string occupies on the printer. Menu item names come from
// restaurant data, so they may arrive decomposed ("u" + combining macron) or
// outside the BMP — both make String.length larger than what actually prints and
// would pull the price column out of alignment.
function printableWidth(text) {
  return [...`${text}`.normalize('NFC').replace(/\p{M}/gu, '')].length;
}

// Right-aligns value against label across the full line width.
function padRow(label, value, lineLength) {
  const padding = Math.max(
    lineLength - printableWidth(label) - printableWidth(value),
    1
  );

  return `${label}${' '.repeat(padding)}${value}`;
}

function getItemSignature(item) {
  const modifiers = (item.modifiers || [])
    .map(modifier => `${modifier.name}:${modifier.price}`)
    .sort()
    .join('|');

  return `${item.name}#${item.price}#${modifiers}`;
}

// Backend may send either one row per unit (no quantity field) or a single row
// with a quantity, so identical rows are grouped and their quantities summed.
function groupItems(items) {
  const grouped = [];
  const indexBySignature = {};

  items.forEach(item => {
    const signature = getItemSignature(item);
    const quantity = Number(item.quantity) || 1;

    if (indexBySignature[signature] === undefined) {
      indexBySignature[signature] = grouped.length;
      grouped.push({ ...item, quantity });
      return;
    }

    grouped[indexBySignature[signature]].quantity += quantity;
  });

  return grouped;
}

// Splits a label into lines. Only the first line has to leave room for the
// price column, so continuation lines get the wider budget and use the paper.
function wrapLabel(label, firstWidth, continuationWidth) {
  const lines = [];
  // Composing first keeps code-point slicing below in step with printableWidth.
  // Splitting on any whitespace matters: a newline or tab inside a menu item
  // name would otherwise stay embedded in a "word" and break the row mid-line,
  // pushing the price column out of alignment.
  const words = `${label}`
    .normalize('NFC')
    .split(/\s+/)
    .filter(Boolean);
  let current = '';

  const widthFor = () => (lines.length === 0 ? firstWidth : continuationWidth);

  words.forEach(word => {
    const candidate = current ? `${current} ${word}` : word;

    if (printableWidth(candidate) <= widthFor()) {
      current = candidate;
      return;
    }

    if (current) {
      lines.push(current);
      current = '';
    }

    // A single word too long for the line is hard-split. The budget is re-read
    // after each push, since the first line is narrower than the rest.
    let rest = word;
    let budget = widthFor();
    while (printableWidth(rest) > budget) {
      const chars = [...rest];
      lines.push(chars.slice(0, budget).join(''));
      rest = chars.slice(budget).join('');
      budget = widthFor();
    }
    current = rest;
  });

  if (current) {
    lines.push(current);
  }

  return lines.length ? lines : [''];
}

// Renders "<label>            <price>" padded to lineLength, wrapping long labels.
function buildRow(prefix, label, price, lineLength) {
  const priceText = formatAmount(price);
  const continuationIndent = ' '.repeat(prefix.length);
  const firstWidth = Math.max(
    lineLength - priceText.length - 1 - prefix.length,
    1
  );
  const continuationWidth = Math.max(lineLength - prefix.length, 1);
  const labelLines = wrapLabel(label, firstWidth, continuationWidth);

  // The price sits on the first line, next to the start of the name; any
  // wrapped remainder follows underneath with no price column.
  return labelLines.map((line, index) =>
    index === 0
      ? padRow(`${prefix}${line}`, priceText, lineLength)
      : `${continuationIndent}${line}`
  );
}

/**
 * Builds the printable, translated list of purchased items.
 * Returns an empty string when there is nothing to print, so callers can pass
 * items unconditionally.
 */
export default function formatReceiptItems(
  items,
  {
    lineLength = DEFAULT_LINE_LENGTH,
    currencyCode = '',
    discount = 0,
    discountCode = '',
  } = {}
) {
  if (!items || !items.length) {
    return '';
  }

  const width = Number(lineLength) || DEFAULT_LINE_LENGTH;
  const separator = SEPARATOR_CHAR.repeat(width);

  const lines = [separator];
  let total = 0;

  groupItems(items).forEach(item => {
    const modifiers = item.modifiers || [];
    const modifiersPrice = modifiers.reduce(
      (sum, modifier) => sum + (Number(modifier.price) || 0),
      0
    );
    // The item row carries the base price only and each modifier is priced on
    // its own row, so the printed column adds up to the total below.
    total += ((Number(item.price) || 0) + modifiersPrice) * item.quantity;

    lines.push(
      ...buildRow(
        `${item.quantity}x `,
        Vue.i18n.translate(item.name),
        (Number(item.price) || 0) * item.quantity,
        width
      )
    );

    modifiers.forEach(modifier => {
      lines.push(
        ...buildRow(
          MODIFIER_PREFIX,
          Vue.i18n.translate(modifier.name),
          (Number(modifier.price) || 0) * item.quantity,
          width
        )
      );
    });
  });

  lines.push(separator);

  const discountAmount = Number(discount) || 0;

  if (discountAmount) {
    // The coupon code doubles as the label when there is one, matching how the
    // discount row is rendered in PaymentInformation.vue.
    const discountLabel =
      discountCode ||
      Vue.i18n.translate('screens.payment.summaryDiscountLabel');
    lines.push(
      padRow(discountLabel, `-${formatAmount(discountAmount)}`, width)
    );
    total -= discountAmount;
  }

  // Currency code rather than the symbol: thermal printers often lack the
  // glyph for narrow symbols such as the euro sign.
  const totalText = currencyCode
    ? `${formatAmount(total)} ${currencyCode}`
    : formatAmount(total);
  const totalLabel = Vue.i18n.translate('kiosk.printer.itemsTotal');
  lines.push(padRow(totalLabel, totalText, width));

  return lines.join('\n');
}
